import { FC, ReactNode, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

/*
 * TODO: after releasing icons, readd this
 * import MyO2Logo from '@o2/icons/assets/logo-mojeo2/MO2white.svg';
 */
import AppLayout, { AppLayoutBottom, AppLayoutHeader, AppLayoutPage, AppLayoutTop } from '../AppLayout';
import AppBar, { AppBarButton, AppBarControls } from '../AppBar';
import { APPBAR_EXT_HEIGHT } from '../constants/layout';
import AppBarInternal from '../AppBarInternal';
import PageHeader from '../PageHeader';

import Skeleton from './Skeleton';
import Typography from '../Typography';

export interface LayoutSkeletonProps {
  /**
   * Number of items in nav menu.
   */
  sidebarItemCount?: number;
  internal?: boolean;
  /**
   * If passed, the page content will be based on the shape of the child component(s).
   */
  children?: ReactNode;
}

const AppBarNavRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignSelf: 'stretch',
  width: '100%',
  justifyContent: 'start',
  marginLeft: '32px',
  [theme.breakpoints.down('lg')]: {
    marginLeft: '8px',
  },
}));

/**
 * Full-page layout skeleton designed to prevent white screens (sometimes with 'Loading...') when loading assets
 * (like the huge i18n JSON from a slow server). This component is supposed to be used outside of any app layout
 * trees as it provides its own structure.
 * @constructor
 * @deprecated
 */
const LayoutSkeleton: FC<LayoutSkeletonProps> = ({ sidebarItemCount = 3, internal = false, children }) => {
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));

  const child = useMemo(() => {
    if (children) {
      return <Skeleton>{children}</Skeleton>;
    } else {
      return (
        <>
          <AppLayoutHeader hasInternalBar={internal}>
            <Skeleton sx={{ mx: 4, my: 2 }} fullWidth={false}>
              <PageHeader showBacklink heading="Dummy heading" perex="Dummy perex" />
            </Skeleton>
          </AppLayoutHeader>
          <Skeleton variant="text" height="180px" sx={{ mx: 4, my: 8 }} />
          <Skeleton variant="text" sx={{ mx: 4 }} width="100%">
            <Typography variant="h2">Dummy text that is a bit longer than the short one.</Typography>
          </Skeleton>
          <Skeleton variant="text" sx={{ mx: 4, mt: 2 }} width="100%">
            <Typography variant="h2">Dummy text that is a bit longer than the short one.</Typography>
          </Skeleton>
        </>
      );
    }
  }, [children, internal]);

  return (
    <AppLayout>
      <AppLayoutTop hasInternalBar={internal}>
        <AppBar internalBar={internal && <AppBarInternal isVisible={internal} />}>
          {/*<AppBarLogo src={MyO2Logo} alt="Moje O2" />*/}
          <Skeleton width="80px" height={`${APPBAR_EXT_HEIGHT[mdDown ? 'xs' : 'md']}px`} />
          <AppBarNavRoot>
            {new Array(sidebarItemCount).fill(null).map((_, i) => (
              <Skeleton key={i} fullWidth={false}>
                <AppBarButton>Link Link Link {i}</AppBarButton>
              </Skeleton>
            ))}
          </AppBarNavRoot>
          <Skeleton width="80px" height={`${APPBAR_EXT_HEIGHT[mdDown ? 'xs' : 'md']}px`}>
            <AppBarControls />
          </Skeleton>
        </AppBar>
      </AppLayoutTop>
      <AppLayoutBottom>
        <AppLayoutPage isInternalPage={internal}>{child}</AppLayoutPage>
      </AppLayoutBottom>
    </AppLayout>
  );
};

export default LayoutSkeleton;
